import React, { useState, useEffect } from 'react';
import { Plus, Search, Filter, Target, Calendar, Clock, Edit, Trash2, Eye, Plus as PlusIcon, X, Save } from 'lucide-react';
import { WorkoutService } from '../lib/workoutService';
import { WorkoutPlanWithSchedule, CreateWorkoutPlanData } from '../types/dataModel';
import { ExerciseService } from '../lib/exerciseService';
import { useAuth } from '../contexts/AuthContext';
import { CreateWorkoutPlanForm } from '../components/CreateWorkoutPlanForm';
import { WorkoutSelector } from '../components/CreateWorkoutPlanForm';
import { LoadingSpinner } from '../components/LoadingSpinner';
import { PAGE_CHROME_LIGHT, PAGE_CHROME_LOADER_BRAND_GREEN } from '../constants/pageChrome';
import { usePageStatusBar } from '../hooks/usePageStatusBar';

export const WorkoutPlans = () => {
  const [workoutPlans, setWorkoutPlans] = useState<WorkoutPlanWithSchedule[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedDifficulty, setSelectedDifficulty] = useState('all');
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [selectedWorkoutPlan, setSelectedWorkoutPlan] = useState<WorkoutPlanWithSchedule | null>(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [editingPlan, setEditingPlan] = useState<WorkoutPlanWithSchedule | null>(null);
  const [originalEditingPlan, setOriginalEditingPlan] = useState<WorkoutPlanWithSchedule | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editingPlanExercises, setEditingPlanExercises] = useState<any[]>([]);
  const [showExerciseSelector, setShowExerciseSelector] = useState(false);
  const [showWorkoutSelector, setShowWorkoutSelector] = useState(false);
  const [availableExercises, setAvailableExercises] = useState<any[]>([]);
  const [availableWorkouts, setAvailableWorkouts] = useState<any[]>([]);
  const [workoutSearchTerm, setWorkoutSearchTerm] = useState('');
  const [selectedWorkoutDifficulty, setSelectedWorkoutDifficulty] = useState('all');
  const [exerciseSearchTerm, setExerciseSearchTerm] = useState('');
  const [selectedExerciseCategory, setSelectedExerciseCategory] = useState('all');
  const { profile } = useAuth();

  usePageStatusBar({
    background: loading ? PAGE_CHROME_LOADER_BRAND_GREEN : PAGE_CHROME_LIGHT,
  });

  // Template update dialog state
  const [showTemplateUpdateDialog, setShowTemplateUpdateDialog] = useState(false);
  const [activeInstances, setActiveInstances] = useState<any[]>([]);
  const [pendingUpdate, setPendingUpdate] = useState<Partial<CreateWorkoutPlanData> | null>(null);
  const [selectedInstanceIds, setSelectedInstanceIds] = useState<Set<string>>(new Set());

  // Fetch workout plans
  useEffect(() => {
    fetchWorkoutPlans();
  }, []);

  // Fetch available exercises when exercise selector is opened
  useEffect(() => {
    if (showExerciseSelector) {
      fetchAvailableExercises();
    }
  }, [showExerciseSelector]);

  // Fetch available workouts when workout selector is opened
  useEffect(() => {
    if (showWorkoutSelector) {
      fetchAvailableWorkouts();
    }
  }, [showWorkoutSelector]);

  const fetchAvailableExercises = async () => {
    try {
      const exercises = await ExerciseService.getExercises();
      setAvailableExercises(exercises);
    } catch (error) {
      console.error('Error fetching exercises:', error);
    }
  };

  const fetchAvailableWorkouts = async () => {
    try {
      const workouts = await WorkoutService.getWorkouts();
      setAvailableWorkouts(workouts);
    } catch (error) {
      console.error('Error fetching workouts:', error);
    }
  };

  const addExerciseToPlan = (exercise: any) => {
    const newExercise = {
      id: `temp-${Date.now()}`, // Temporary ID for new exercises
      workoutPlanId: editingPlan?.id || '',
      exerciseId: exercise.id,
      dayNumber: 1,
      exerciseOrder: editingPlanExercises.length + 1,
      sets: 3,
      reps: 10,
      durationSeconds: undefined,
      restSeconds: 60,
      notes: '',
      exerciseName: exercise.name,
      exerciseDescription: exercise.description,
      exerciseCategory: exercise.category,
      exerciseMuscleGroups: exercise.muscleGroups,
      exerciseEquipment: exercise.equipment,
      exerciseDifficulty: exercise.difficulty,
      exerciseInstructions: exercise.instructions,
      exerciseImageUrl: exercise.imageUrl
    };

    setEditingPlanExercises(prev => [...prev, newExercise]);
    setShowExerciseSelector(false);
  };

  const fetchWorkoutPlans = async () => {
    try {
      setLoading(true);
      setError(null);
      const plans = await WorkoutService.getWorkoutPlans();
      setWorkoutPlans(plans);
    } catch (err) {
      console.error('Error fetching workout plans:', err);
      setError('Failed to load workout plans. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateWorkoutPlan = async (planData: CreateWorkoutPlanData) => {
    setIsCreating(true);
    try {
      // Create the workout plan with workout assignments
      const newWorkoutPlan = await WorkoutService.createWorkoutPlan(planData);
      
      console.log('Workout plan created successfully:', newWorkoutPlan);
      setShowCreateForm(false);
      
      // Refresh the workout plans list
      await fetchWorkoutPlans();
    } catch (error) {
      console.error('Error creating workout plan:', error);
      setError('Failed to create workout plan. Please try again.');
    } finally {
      setIsCreating(false);
    }
  };

  const handleViewDetails = async (plan: WorkoutPlanWithSchedule) => {
    try {
      const detailedPlan = await WorkoutService.getWorkoutPlanById(plan.id);
      if (detailedPlan) {
        setSelectedWorkoutPlan(detailedPlan);
        setShowDetailModal(true);
      }
    } catch (error) {
      console.error('Error fetching workout plan details:', error);
      setError('Failed to load workout plan details. Please try again.');
    }
  };

  const handleEditPlan = async (plan: WorkoutPlanWithSchedule) => {
    try {
      // Fetch the detailed workout plan with workouts
      const detailedPlan = await WorkoutService.getWorkoutPlanById(plan.id);
      if (detailedPlan) {
        console.log('Detailed plan for editing:', detailedPlan);
        setEditingPlan(detailedPlan);
        setOriginalEditingPlan(JSON.parse(JSON.stringify(detailedPlan)));
        setIsEditing(false);
      }
    } catch (error) {
      console.error('Error fetching workout plan details for editing:', error);
      setError('Failed to load workout plan details for editing. Please try again.');
    }
  };

  const handleSaveEdit = async (updatedData: Partial<CreateWorkoutPlanData>) => {
    if (!editingPlan) return;

    // No changes - close without updating
    if (originalEditingPlan) {
      const nameSame = (updatedData.name ?? editingPlan.name) === originalEditingPlan.name;
      const descSame = (updatedData.description ?? editingPlan.description ?? '') === (originalEditingPlan.description ?? '');
      const diffSame = (updatedData.difficulty ?? editingPlan.difficulty ?? '') === (originalEditingPlan.difficulty ?? '');
      const daysSame = (updatedData.totalNoOfDays ?? editingPlan.totalNoOfDays ?? 28) === (originalEditingPlan.totalNoOfDays ?? 28);
      const publicSame = (updatedData.isPublic ?? editingPlan.isPublic) === originalEditingPlan.isPublic;
      if (nameSame && descSame && diffSame && daysSame && publicSame) {
        setEditingPlan(null);
        setOriginalEditingPlan(null);
        return;
      }
    }

    try {
      setIsEditing(true);
      
      // Check if this is a template and if it has active instances
      console.log('🔍 Checking for active instances...');
      const instances = await WorkoutService.getActiveInstancesForTemplate(editingPlan.id);
      
      if (instances.length > 0) {
        // Show dialog to ask trainer if they want to update instances too
        console.log(`📋 Found ${instances.length} active instances`);
        setActiveInstances(instances);
        setSelectedInstanceIds(new Set());
        setPendingUpdate(updatedData);
        setShowTemplateUpdateDialog(true);
        setIsEditing(false);
        return; // Wait for user decision
      }

      // No active instances, proceed with template-only update
      await performTemplateUpdate(updatedData, []);
    } catch (error) {
      console.error('Error updating workout plan:', error);
      setError('Failed to update workout plan. Please try again.');
      setIsEditing(false);
    }
  };

  const performTemplateUpdate = async (updatedData: Partial<CreateWorkoutPlanData>, instanceIdsToSync: string[]) => {
    if (!editingPlan) return;

    try {
      setIsEditing(true);
      
      // Update the workout plan template
      await WorkoutService.updateWorkoutPlan(editingPlan.id, updatedData);
      console.log('✅ Template updated');

      if (instanceIdsToSync.length > 0) {
        console.log(`🔄 Syncing ${instanceIdsToSync.length} instances...`);
        await WorkoutService.syncMultipleInstancesWithTemplate(instanceIdsToSync);
        console.log('✅ Instances synced');
      }
      
      // Refresh the list
      await fetchWorkoutPlans();
      
      // Reset editing state
      setEditingPlan(null);
      setOriginalEditingPlan(null);
      setEditingPlanExercises([]);
      setIsEditing(false);
      setShowTemplateUpdateDialog(false);
      setActiveInstances([]);
      setPendingUpdate(null);
      setSelectedInstanceIds(new Set());
      
      // Show success message
      console.log('Workout plan updated successfully!');
    } catch (error) {
      console.error('Error updating workout plan:', error);
      setError('Failed to update workout plan. Please try again.');
      setIsEditing(false);
    }
  };

  const handleTemplateOnlyUpdate = async () => {
    if (!pendingUpdate) return;
    await performTemplateUpdate(pendingUpdate, []);
  };

  const handleUpdateWithSelectedMembers = async () => {
    if (!pendingUpdate) return;
    await performTemplateUpdate(pendingUpdate, Array.from(selectedInstanceIds));
  };

  const handleDeleteWorkoutPlan = async (id: string) => {
    if (window.confirm('Are you sure you want to delete this workout plan? This action cannot be undone.')) {
      try {
        await WorkoutService.deleteWorkoutPlan(id);
        await fetchWorkoutPlans(); // Refresh the list
      } catch (error) {
        console.error('Error deleting workout plan:', error);
        setError('Failed to delete workout plan. Please try again.');
      }
    }
  };

  const getDifficultyColor = (difficulty: string) => {
    switch (difficulty) {
      case 'beginner': return 'text-green-600 bg-green-100';
      case 'intermediate': return 'text-yellow-600 bg-yellow-100';
      case 'advanced': return 'text-red-600 bg-red-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };


  // Filter workout plans based on search and difficulty
  const filteredWorkoutPlans = workoutPlans.filter(plan => {
    const matchesSearch = plan.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         (plan.description && plan.description.toLowerCase().includes(searchTerm.toLowerCase()));
    const matchesDifficulty = selectedDifficulty === 'all' || plan.difficulty === selectedDifficulty;
    return matchesSearch && matchesDifficulty;
  });

  if (loading) {
    return <LoadingSpinner fullScreen />;
  }

  if (error) {
    return (
      <div className="min-h-screen py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <div className="text-red-600 text-lg mb-4">⚠️ {error}</div>
            <button 
              onClick={fetchWorkoutPlans}
              className="btn-primary"
            >
              Try Again
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl font-heading font-bold text-gray-900 mb-4">
            Workout Plans
          </h1>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            Discover and manage workout plans designed to help you achieve your fitness goals
          </p>
        </div>

        {/* Search and Filter Bar */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-8">
          <div className="flex flex-col md:flex-row gap-4 items-center justify-between">
            <div className="flex-1 max-w-md">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <input
                  type="text"
                  placeholder="Search workout plans..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
              </div>
            </div>
            
            <div className="flex items-center gap-4">
              <select
                value={selectedDifficulty}
                onChange={(e) => setSelectedDifficulty(e.target.value)}
                className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              >
                <option value="all">All Difficulties</option>
                <option value="beginner">Beginner</option>
                <option value="intermediate">Intermediate</option>
                <option value="advanced">Advanced</option>
              </select>

              {profile?.role === 'trainer' && (
                <button
                  onClick={() => setShowCreateForm(true)}
                  className="btn-primary inline-flex items-center gap-2"
                >
                  <PlusIcon className="w-5 h-5" />
                  Create Plan
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Workout Plans Grid */}
        {filteredWorkoutPlans.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredWorkoutPlans.map((plan) => (
              <div key={plan.id} className="card hover:shadow-xl transition-shadow duration-300">
                {/* Tags Row */}
                <div className="flex items-center gap-2 mb-3">
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${getDifficultyColor(plan.difficulty || 'beginner')}`}>
                    {plan.difficulty}
                  </span>
                  {/* TODO: Add access type when implemented */}
                </div>

                {/* Title */}
                <h3 className="text-xl font-semibold text-gray-900 mb-3 truncate">{plan.name}</h3>

                {/* Description */}
                <div className="mb-4 h-12">
                  {plan.description ? (
                    <p className="text-gray-600 text-sm leading-6 line-clamp-2">{plan.description}</p>
                  ) : (
                    <div className="h-12"></div>
                  )}
                </div>

                {/* Stats */}
                <div className="grid grid-cols-2 gap-4 mb-4">
                  <div className="flex items-center gap-2 text-sm text-gray-600">
                    <Calendar className="w-4 h-4" />
                    <span>{Math.ceil((plan.totalNoOfDays || 28) / 7)} week{Math.ceil((plan.totalNoOfDays || 28) / 7) !== 1 ? 's' : ''} ({plan.totalNoOfDays || 28} days)</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-gray-600">
                    <Clock className="w-4 h-4" />
                    <span>{plan.planWorkouts?.length || 0} workouts</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-gray-600">
                    <Target className="w-4 h-4" />
                    <span>{plan.planWorkouts?.reduce((total: number, w: any) => total + (w.workout?.workoutBlocks?.reduce((blockTotal: number, block: any) => blockTotal + (block.exerciseSets?.length || 0), 0) || 0), 0) || 0} exercises</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-gray-600">
                    <div className="w-2 h-2 rounded-full bg-primary-600"></div>
                    <span>{plan.isPublic ? 'Public' : 'Private'}</span>
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="flex gap-2 pt-4 border-t border-gray-100">
                  <button 
                    onClick={() => handleViewDetails(plan)}
                    className="flex-1 btn-primary text-sm py-2 flex items-center justify-center gap-2"
                  >
                    <Eye className="w-4 h-4" />
                    View Details
                  </button>
                  
                  <button 
                        onClick={() => plan.id && handleEditPlan(plan)}
                    className="px-3 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 transition-colors"
                  >
                    <Edit className="w-4 h-4" />
                  </button>
                  <button 
                    onClick={() => handleDeleteWorkoutPlan(plan.id)}
                    className="px-3 py-2 bg-red-100 text-red-700 rounded-md hover:bg-red-200 transition-colors"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-16">
            <Target className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-xl font-medium text-gray-900 mb-2">No workout plans found</h3>
            <p className="text-gray-600 mb-6">
              {searchTerm || selectedDifficulty !== 'all' 
                ? 'Try adjusting your search or filters'
                : 'Get started by creating your first workout plan'
              }
            </p>
            {profile?.role === 'trainer' && !searchTerm && selectedDifficulty === 'all' && (
              <button
                onClick={() => setShowCreateForm(true)}
                className="btn-primary inline-flex items-center gap-2"
              >
                <PlusIcon className="w-5 h-5" />
                Create Your First Plan
              </button>
            )}
          </div>
        )}

        {/* Create Workout Plan Modal */}
        {showCreateForm && (
          <CreateWorkoutPlanForm
            isOpen={showCreateForm}
            onClose={() => setShowCreateForm(false)}
            onSubmit={handleCreateWorkoutPlan}
          />
        )}

        {/* Workout Plan Detail Modal */}
        {showDetailModal && selectedWorkoutPlan && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
              {/* Header */}
              <div className="sticky top-0 bg-white border-b border-gray-200 p-6 rounded-t-xl">
                <div className="flex items-center justify-between">
                  <h2 className="text-2xl font-bold text-gray-900">{selectedWorkoutPlan.name}</h2>
                  <button 
                    onClick={() => setShowDetailModal(false)} 
                    className="p-2 hover:bg-gray-100 rounded-full transition-colors"
                  >
                    <X className="w-6 h-6 text-gray-500" />
                  </button>
                </div>
              </div>

              {/* Content */}
              <div className="p-6 space-y-6">
                {/* Plan Info */}
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-3">Plan Details</h3>
                    <div className="space-y-2">
                      <div className="flex items-center gap-2">
                        <span className="text-gray-600">Difficulty:</span>
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${getDifficultyColor(selectedWorkoutPlan.difficulty || 'beginner')}`}>
                          {selectedWorkoutPlan.difficulty}
                        </span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-gray-600">Duration:</span>
                        <span>{Math.ceil((selectedWorkoutPlan.totalNoOfDays || 28) / 7)} week{Math.ceil((selectedWorkoutPlan.totalNoOfDays || 28) / 7) !== 1 ? 's' : ''} ({selectedWorkoutPlan.totalNoOfDays || 28} days)</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-gray-600">Workouts per week:</span>
                        <span>{selectedWorkoutPlan.planWorkouts?.length || 0}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-gray-600">Status:</span>
                        <span className={selectedWorkoutPlan.isPublic ? 'text-green-600' : 'text-gray-600'}>
                          {selectedWorkoutPlan.isPublic ? 'Public' : 'Private'}
                        </span>
                      </div>
                    </div>
                  </div>
                  
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-3">Description</h3>
                    <p className="text-gray-600">
                      {selectedWorkoutPlan.description || 'No description provided.'}
                    </p>
                  </div>
                </div>

                {/* Exercises */}
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Workouts ({selectedWorkoutPlan.planWorkouts?.length || 0})</h3>
                  {selectedWorkoutPlan.planWorkouts && selectedWorkoutPlan.planWorkouts.length > 0 ? (
                    <div className="space-y-4">
                      {selectedWorkoutPlan.planWorkouts?.map((workoutAssignment: any, index: number) => (
                        <div key={workoutAssignment.id} className="border border-gray-200 rounded-lg p-4">
                          <div className="flex items-start justify-between mb-3">
                            <div className="flex-1">
                              <h4 className="font-medium text-gray-900">{workoutAssignment.workout?.name || 'Unknown Workout'}</h4>
                              <p className="text-sm text-gray-600">{workoutAssignment.workout?.note || 'No description available'}</p>
                            </div>
                            <div className="text-right text-sm text-gray-500">
                              <div>Week {workoutAssignment.weekNumber}</div>
                              <div>Day {workoutAssignment.dayNumber}</div>
                              <div>Order {workoutAssignment.workoutOrder}</div>
                            </div>
                          </div>
                          
                          {workoutAssignment.notes && (
                            <div className="mt-3 pt-3 border-t border-gray-100">
                              <span className="text-gray-600 text-sm">Notes: </span>
                              <span className="text-sm">{workoutAssignment.notes}</span>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-gray-500 text-center py-8">No exercises added to this workout plan yet.</p>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Exercise Selector Modal */}
        {showExerciseSelector && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-[60]">
            <div className="bg-white rounded-xl max-w-4xl w-full max-h-[80vh] overflow-y-auto">
              <div className="sticky top-0 bg-white border-b border-gray-200 p-6">
                <div className="flex items-center justify-between">
                  <h3 className="text-xl font-bold text-gray-900">Select Exercises</h3>
                  <button onClick={() => setShowExerciseSelector(false)} className="p-2 hover:bg-gray-100 rounded-full transition-colors">
                    <X className="w-6 h-6 text-gray-500" />
                  </button>
                </div>

                {/* Search and Filter */}
                <div className="mt-4 space-y-4">
                  <input
                    type="text"
                    placeholder="Search exercises..."
                    value={exerciseSearchTerm}
                    onChange={(e) => setExerciseSearchTerm(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  />
                  <select 
                    value={selectedExerciseCategory}
                    onChange={(e) => setSelectedExerciseCategory(e.target.value)}
                    className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  >
                    <option value="all">All Categories</option>
                    <option value="strength">Strength</option>
                    <option value="cardio">Cardio</option>
                    <option value="flexibility">Flexibility</option>
                    <option value="balance">Balance</option>
                  </select>
                </div>
              </div>

              <div className="p-6">
                <div className="grid gap-4">
                  {availableExercises
                    .filter(exercise => {
                      const matchesSearch = exercise.name.toLowerCase().includes(exerciseSearchTerm.toLowerCase()) ||
                                           exercise.description.toLowerCase().includes(exerciseSearchTerm.toLowerCase());
                      const matchesCategory = selectedExerciseCategory === 'all' || exercise.category === selectedExerciseCategory;
                      return matchesSearch && matchesCategory;
                    })
                    .map((exercise) => (
                      <div key={exercise.id} className="flex items-center justify-between p-4 border border-gray-200 rounded-lg hover:bg-gray-50">
                        <div className="flex-1">
                          <h4 className="font-medium text-gray-900">{exercise.name}</h4>
                          <p className="text-sm text-gray-600">{exercise.description}</p>
                          <div className="flex items-center gap-2 mt-2">
                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                              exercise.difficulty === 'beginner' ? 'text-green-600 bg-green-100' :
                              exercise.difficulty === 'intermediate' ? 'text-yellow-600 bg-yellow-100' :
                              'text-red-600 bg-red-100'
                            }`}>
                              {exercise.difficulty}
                            </span>
                            <span className="text-xs text-gray-500 capitalize">{exercise.category}</span>
                          </div>
                        </div>
                        <button
                          onClick={() => addExerciseToPlan(exercise)}
                          className="px-4 py-2 bg-[var(--action-primary)] text-[var(--ink)] rounded-md hover:bg-[var(--action-primary-pressed)] transition-colors"
                        >
                          Add
                        </button>
                      </div>
                    ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Edit Workout Plan Modal */}
        {editingPlan && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-xl max-w-6xl w-full max-h-[90vh] overflow-y-auto">
              {/* Header */}
              <div className="sticky top-0 bg-white border-b border-gray-200 p-6 rounded-t-xl">
                <div className="flex items-center justify-between">
                  <h2 className="text-2xl font-bold text-gray-900">Edit Workout Plan</h2>
                  <button 
                    onClick={() => { setEditingPlan(null); setOriginalEditingPlan(null); }} 
                    className="p-2 hover:bg-gray-100 rounded-full transition-colors"
                  >
                    <X className="w-6 h-6 text-gray-500" />
                  </button>
                </div>
              </div>

              {/* Edit Form */}
              <div className="p-6 space-y-6">
                {/* Basic Information */}
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Workout Plan Name *
                    </label>
                    <input
                      type="text"
                      value={editingPlan.name}
                      onChange={(e) => setEditingPlan({...editingPlan, name: e.target.value})}
                      maxLength={255}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      placeholder="e.g., Beginner Strength Program"
                    />
                    <div className="flex justify-between items-center mt-1">
                      <span className="text-xs text-gray-500">
                        {editingPlan.name.length}/255 characters
                      </span>
                      {editingPlan.name.length > 200 && (
                        <span className="text-xs text-orange-500">
                          Getting close to limit
                        </span>
                      )}
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Difficulty Level *
                    </label>
                    <select
                      value={editingPlan.difficulty}
                      onChange={(e) => setEditingPlan({...editingPlan, difficulty: e.target.value as 'beginner' | 'intermediate' | 'advanced'})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    >
                      <option value="beginner">Beginner</option>
                      <option value="intermediate">Intermediate</option>
                      <option value="advanced">Advanced</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Description
                  </label>
                  <textarea
                    value={editingPlan.description || ''}
                    onChange={(e) => setEditingPlan({...editingPlan, description: e.target.value})}
                    maxLength={1000}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    placeholder="Describe the workout plan, goals, and target audience..."
                  />
                  <div className="flex justify-between items-center mt-1">
                    <span className="text-xs text-gray-500">
                      {(editingPlan.description || '').length}/1000 characters
                    </span>
                    {(editingPlan.description || '').length > 800 && (
                      <span className="text-xs text-orange-500">
                        Getting close to limit
                      </span>
                    )}
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Duration (Weeks) *
                    </label>
                    <input
                      type="number"
                      min="1"
                      max="52"
                      value={editingPlan.totalNoOfDays || 28}
                      onChange={(e) => setEditingPlan({...editingPlan, totalNoOfDays: parseInt(e.target.value) || 28})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    />
                  </div>

                                      {/* Workouts per week field removed - now managed through workout assignments */}
                </div>

                {/* Public/Private Toggle */}
                <div>
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={editingPlan.isPublic}
                      onChange={(e) => setEditingPlan({...editingPlan, isPublic: e.target.checked})}
                      className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                    />
                    <span className="text-sm font-medium text-gray-700">
                      Make this workout plan public (visible to all users)
                    </span>
                  </label>
                </div>

                {/* Workout Management */}
                <div>
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-semibold text-gray-900">Workouts ({editingPlan?.planWorkouts?.length || 0})</h3>
                    <button
                      type="button"
                      onClick={() => setShowWorkoutSelector(true)}
                      className="btn-primary inline-flex items-center gap-2"
                    >
                      <Plus className="w-4 h-4" />
                      Add Workout
                    </button>
                  </div>

                  {/* Current Workouts */}
                  {editingPlan?.planWorkouts && editingPlan.planWorkouts.length > 0 ? (
                    <div className="space-y-4">
                      {editingPlan.planWorkouts?.map((workoutAssignment: any, index: number) => (
                        <div key={workoutAssignment.id} className="border border-gray-200 rounded-lg p-4">
                          <div className="flex items-start justify-between mb-3">
                            <div className="flex-1">
                              <h4 className="font-medium text-gray-900">{workoutAssignment.workout?.name || 'Unknown Workout'}</h4>
                              <p className="text-sm text-gray-600">{workoutAssignment.workout?.note || 'No description'}</p>
                            </div>
                            <div className="flex items-center gap-2">
                              <button
                                type="button"
                                onClick={() => {
                                  // TODO: Implement workout reordering
                                  console.log('Workout reordering not yet implemented');
                                }}
                                disabled={index === 0}
                                className="p-1 text-gray-400 hover:text-gray-600 disabled:opacity-50"
                              >
                                ↑
                              </button>
                              <button
                                type="button"
                                onClick={() => {
                                  // TODO: Implement workout reordering
                                  console.log('Workout reordering not yet implemented');
                                }}
                                disabled={index === (editingPlan.planWorkouts?.length || 0) - 1}
                                className="p-1 text-gray-400 hover:text-gray-600 disabled:opacity-50"
                              >
                                ↓
                              </button>
                              <button
                                type="button"
                                onClick={() => {
                                  // TODO: Implement workout removal
                                  console.log('Workout removal not yet implemented');
                                }}
                                className="p-1 text-red-500 hover:text-red-700"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </div>
                          </div>
                          
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                            <div>
                              <label className="block text-xs font-medium text-gray-700 mb-1">Week</label>
                              <input
                                type="number"
                                min="1"
                                max={editingPlan.totalNoOfDays || 28}
                                value={workoutAssignment.weekNumber}
                                onChange={(e) => {
                                  // TODO: Implement week number update
                                  console.log('Week number update not yet implemented');
                                }}
                                className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-primary-500"
                              />
                            </div>
                            <div>
                              <label className="block text-xs font-medium text-gray-700 mb-1">Day</label>
                              <input
                                type="number"
                                min="1"
                                max="7"
                                value={workoutAssignment.dayNumber}
                                onChange={(e) => {
                                  // TODO: Implement day number update
                                  console.log('Day number update not yet implemented');
                                }}
                                className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-primary-500"
                              />
                            </div>
                            <div>
                              <label className="block text-xs font-medium text-gray-700 mb-1">Order</label>
                              <input
                                type="number"
                                min="1"
                                value={workoutAssignment.workoutOrder}
                                onChange={(e) => {
                                  // TODO: Implement workout order update
                                  console.log('Workout order update not yet implemented');
                                }}
                                className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-primary-500"
                              />
                            </div>
                            <div>
                              <label className="block text-xs font-medium text-gray-700 mb-1">Notes</label>
                              <input
                                type="text"
                                value={workoutAssignment.notes || ''}
                                onChange={(e) => {
                                  // TODO: Implement notes update
                                  console.log('Notes update not yet implemented');
                                }}
                                className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-primary-500"
                                placeholder="Optional"
                              />
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-8 border-2 border-dashed border-gray-300 rounded-lg">
                      <Target className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                      <h4 className="text-lg font-medium text-gray-900 mb-2">No workouts assigned yet</h4>
                      <p className="text-gray-600 mb-4">Click "Add Workout" to start building your workout plan</p>
                    </div>
                  )}
                </div>

                {/* Action Buttons */}
                <div className="flex gap-3 pt-4 border-t border-gray-200">
                  <button 
                    type="button" 
                    onClick={() => { setEditingPlan(null); setOriginalEditingPlan(null); }} 
                    className="px-6 py-3 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
                  >
                    Cancel
                  </button>
                  <button 
                    onClick={() => handleSaveEdit({
                      name: editingPlan.name,
                      description: editingPlan.description,
                      difficulty: editingPlan.difficulty,
                      totalNoOfDays: editingPlan.totalNoOfDays || 28,
                      isPublic: editingPlan.isPublic
                    })}
                    disabled={isEditing} 
                    className="flex-1 btn-primary flex items-center justify-center gap-2"
                  >
                    {isEditing ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                        Updating...
                      </>
                    ) : (
                      <>
                        <Save className="w-4 h-4" />
                        Update Workout Plan
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Workout Selector Modal */}
        {showWorkoutSelector && (
          <WorkoutSelector
            onClose={() => setShowWorkoutSelector(false)}
            onCreateWorkout={() => {
              // Not supported in this plan-edit flow yet.
              // (Create flow is implemented in CreateWorkoutPlanForm; keeping this a no-op avoids breaking the selector contract.)
            }}
            onWorkoutSelect={(workout: any) => {
              // Add workout to the editing plan
              if (editingPlan) {
                const newPlanWorkout: any = {
                  id: `temp-${Date.now()}`,
                  workoutPlanId: editingPlan.id,
                  workoutId: workout.id,
                  weekNumber: 1,
                  dayOfWeek: 'Monday' as const,
                  order: (editingPlan.planWorkouts?.length || 0) + 1,
                  notes: '',
                  createdAt: new Date().toISOString(),
                  updatedAt: new Date().toISOString(),
                  workout: workout
                };
                
                setEditingPlan((prev: WorkoutPlanWithSchedule | null) => {
                  if (!prev) return null;
                  return {
                    ...prev,
                    planWorkouts: [...(prev.planWorkouts || []), newPlanWorkout] as any
                  };
                });
              }
              setShowWorkoutSelector(false);
            }}
            availableWorkouts={availableWorkouts}
            searchTerm={workoutSearchTerm}
            setSearchTerm={setWorkoutSearchTerm}
            selectedDifficulty={selectedWorkoutDifficulty}
            setSelectedDifficulty={setSelectedWorkoutDifficulty}
          />
        )}

        {/* Template Update Dialog */}
        {showTemplateUpdateDialog && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[120] p-4">
            <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
              <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between">
                <h2 className="text-lg font-semibold text-gray-900">Update Plan</h2>
                <button
                  onClick={() => {
                    setShowTemplateUpdateDialog(false);
                    setActiveInstances([]);
                    setPendingUpdate(null);
                    setSelectedInstanceIds(new Set());
                    setIsEditing(false);
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="p-4">
                <p className="text-sm text-gray-600 mb-3">
                  There are {activeInstances.length} {activeInstances.length === 1 ? 'person' : 'people'} currently assigned to this plan. Do you want to apply the changes to their workout plans?
                </p>

                <div className="mb-4 max-h-40 overflow-y-auto border border-gray-200 rounded-md">
                  <div className="p-2 flex items-center gap-2 border-b border-gray-100 bg-gray-50">
                    <button
                      type="button"
                      onClick={() => setSelectedInstanceIds(new Set(activeInstances.map((i) => i.instanceId)))}
                      className="text-xs text-purple-600 hover:underline"
                    >
                      All
                    </button>
                    <span className="text-gray-300">|</span>
                    <button
                      type="button"
                      onClick={() => setSelectedInstanceIds(new Set())}
                      className="text-xs text-purple-600 hover:underline"
                    >
                      None
                    </button>
                  </div>
                  {activeInstances.map((instance) => (
                    <label
                      key={instance.instanceId}
                      className="flex items-center gap-3 px-3 py-2 hover:bg-gray-50 cursor-pointer border-b border-gray-50 last:border-0"
                    >
                      <input
                        type="checkbox"
                        checked={selectedInstanceIds.has(instance.instanceId)}
                        onChange={(e) => {
                          const next = new Set(selectedInstanceIds);
                          if (e.target.checked) next.add(instance.instanceId);
                          else next.delete(instance.instanceId);
                          setSelectedInstanceIds(next);
                        }}
                        className="rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                      />
                      <span className="text-sm font-medium text-gray-900 truncate">{instance.memberName}</span>
                      <span className="text-xs text-gray-500 ml-auto">
                        {new Date(instance.startDate).toLocaleDateString()}
                      </span>
                    </label>
                  ))}
                </div>

                <div className="flex gap-2">
                  <button
                    onClick={handleTemplateOnlyUpdate}
                    disabled={isEditing}
                    className="flex-1 px-3 py-2 text-sm bg-purple-600 text-white rounded-md hover:bg-purple-700 disabled:opacity-50 font-medium"
                  >
                    {isEditing ? '...' : 'Template only'}
                  </button>
                  <button
                    onClick={handleUpdateWithSelectedMembers}
                    disabled={isEditing || selectedInstanceIds.size === 0}
                    className="flex-1 px-3 py-2 text-sm bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isEditing ? '...' : `Apply to ${selectedInstanceIds.size} selected`}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default WorkoutPlans; 